import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useOutletContext } from 'react-router-dom';

import { Segmented, Spin } from 'antd';
import css from '@/pages/Login/Login.module.scss';

import { AUTH_ROUTE, AUTH_TYPES as AUTH_TYPES_SHARED } from './constants';

import { selectCompany } from '@shared/store/company/selectors';
import { domainCompanyid } from '@shared/utils/domainCompanyId';


import PropTypes from 'prop-types';

export const AUTH_TYPES_DEFAULT = {
  INTERNAL: 'INTERNAL', // Внутренняя авторизация (Login/Password)
  MOBILE: 'MOBILE', // Внутренняя авторизация по моб. телефону (Phone/Password)
  LDAP: 'LDAP',
  OAUTH: 'OAUTH', // Внешняя авторизация OAUTH (пример вход через: google, yandex, etc.)
  SAP: 'SAP',
};

const AUTH_TYPES = AUTH_TYPES_SHARED || AUTH_TYPES_DEFAULT;

// LoginWrapper - Это оболочка для авторизации
const LoginWrapper = ({ children, withoutNavigation = false, type, isAdmin }) => {
  const { t: t } = useTranslation('authorization');
  const { t: tRegistr } = useTranslation('registration');
  const { t: tRecover } = useTranslation('login');

  const { imageBackgroundLogin, isLoadingImageLogo, imageLogo } = useOutletContext(); // Берём функции/переменные из pages/Login/index.jsx
  const navigate = useNavigate();
  const { pathname, state } = useLocation();

  const [loginProvider, setLoginProvider] = useState(state?.switchFrom ?? AUTH_TYPES?.SAP);

  const [readInfo, changeReadInfo] = useState(false); // Старый state решил оставить на всякий случай (ранее отвечал за поведение окна информации пример KAM-3615)

  const { company } = useSelector(selectCompany);

  /* ПЕРЕЧЕНЬ PROVIDER'ов */
  // internalProvider - Проверка, что в компании есть внутренняя авторизация по email/password
  const internalProvider = company?.providers.find(
    provider => provider.type === AUTH_TYPES.INTERNAL && !provider.external
  );
  const sapProvider = company?.providers.find(provider => provider.type === AUTH_TYPES.SAP && !provider.external);
  const mobileProvider = company.providers.find(provider => provider.type === AUTH_TYPES.MOBILE);
  const ldapLogin = company.providers.find(provider => provider.type === AUTH_TYPES.LDAP);
  const onlyMobileProvider = mobileProvider && !internalProvider;

  useEffect(() => {
    if (
      !document.location.pathname?.includes('mobile') &&
      !document.location.pathname?.includes('sap') &&
      sapProvider &&
      mobileProvider
    ) {
      setLoginProvider(AUTH_TYPES.MOBILE);
      navigate('/user-login/mobile', { state });
    } else if (mobileProvider && sapProvider) {
      const currentMethod = pathname.includes('/mobile') ? AUTH_TYPES.MOBILE : AUTH_TYPES.SAP;

      setLoginProvider(currentMethod);
    } else {
      const state = { switchFrom: loginProvider };

      const providerTypes = company?.providers?.map(item => item.type) || [];
      const [currentMethod] = providerTypes;

      setLoginProvider(currentMethod);
      navigate(AUTH_ROUTE[type]?.[currentMethod], { state });
      handleChangeSegment(currentMethod);
    }
  }, [pathname]);

  const handleCantSignInClick = () => {
    if (company?.supportLink) {
      window.open(company?.supportLink, '_blank');
    } else {
      changeReadInfo(!readInfo); // Оставил старую логику, если нет ссылки на поддержку
    }
  };

  const navigationAuth = (
    <>
      {ldapLogin && (
        <div className={css['LoginPage-label']}>
          <a href={company?.supportLink} className={css.LoginPage__toInfo} onClick={handleCantSignInClick}>
            {t('cantSingIn')}
          </a>
        </div>
      )}
      {!isAdmin && loginProvider === AUTH_TYPES.MOBILE && (
        <div className={css['LoginPage-label']}>
          <div
            id='btnRecoverPassword'
            className={css.LoginPage__toInfo}
            onClick={() => navigate('/user-login/mobile-recovery')}
          >
            {t('recoverPassword')}
          </div>
        </div>
      )}
      {!isAdmin && (internalProvider || mobileProvider) && company?.hasRegistration && (
        <div className={css['LoginPage-label']}>
          <div
            id='btnRegistration'
            className={css.LoginPage__toInfo}
            onClick={() =>
              navigate(onlyMobileProvider ? '/user-login/mobile-registration' : '/user-login/registration')
            }
          >
            {t('registration')}
          </div>
        </div>
      )}
    </>
  );

  const handleChangeRenderPage = flag => {
    navigate(`/user-login/${flag}`);
  };

  const navigationRecover = (
    <div className={css['LoginPage-label']}>
      <div className={css.LoginPage__toInfo} onClick={() => handleChangeRenderPage('auth')}>
        {tRecover('goBack')}
      </div>
    </div>
  );

  const handleChangeSegment = value => {
    setLoginProvider(value);

    const state = { switchFrom: loginProvider };

    // Переключаем на нужную ссылку логина
    // Для корректого отображения segmented необходимо прокидывать state при navigate
    switch (value) {
      case AUTH_TYPES.SAP:
        if (type === 'auth') {
          navigate('/user-login/sap', { state });
        }

        break;

      case AUTH_TYPES.MOBILE:
        if (type === 'auth') {
          navigate('/user-login/mobile', { state });
        }
        if (type === 'recover') {
          navigate('/user-login/mobile-recovery', { state });
        }

        break;

      default:
        break;
    }
  };

  const navigation = () => {
    if (!withoutNavigation) {
      switch (type) {
        case 'auth':
          return navigationAuth;

        case 'recover':
          return navigationRecover;

        default:
          return null;
      }
    }

    return null;
  };

  const SubTitleRender = () => {
    if (loginProvider === AUTH_TYPES.SAP) {
      return (
        <div className={css['LoginPage-titleBody']}>
          {tRecover('recoveryDescription1')} <br />
          {tRecover('recoveryDescription2')}
        </div>
      );
    }
    if (loginProvider === AUTH_TYPES.MOBILE) {
      return <div className={css['LoginPage-titleBody']}>{tRecover('recoveryDescription3')}</div>;
    }
  };

  return (
    <div className={css['App-card-item']}>
      {imageBackgroundLogin && <img className={css.imageBackgroundLogin} src={imageBackgroundLogin} alt='' />}
      <div className={css['LoginPage-form__content']}>
        {type === 'auth' && (
          <div className={css['LoginPage-header']}>
            <Spin spinning={isLoadingImageLogo} wrapperClassName={css['LoginPage-header__logo']}>
              {!isLoadingImageLogo && <img src={imageLogo} alt='' />}
            </Spin>
            <div className={css['LoginPage-header__subtitle']}>
              {/* в рамках задачи CLOUD-611 */}
              {/* {domainCompanyid.ABSOLUTE_ID === company.id ? t('subtitle_2') : t('subtitle')} */}
              {domainCompanyid.ABSOLUTE_ID === company.id && t('subtitle_2')}
            </div>
          </div>
        )}

        {type === 'registr' && (
          <div className={css['LoginPage-header']}>
            <Spin spinning={isLoadingImageLogo} wrapperClassName={css['LoginPage-header__logo']}>
              {!isLoadingImageLogo && <img src={imageLogo} alt='' />}
            </Spin>
            {company?.id !== domainCompanyid.ABSOLUTE && (
              <div className={css['LoginPage-slogan']}>{tRegistr('smart')}</div>
            )}
            <div className={css['LoginPage-header__subtitle']}>{tRegistr('registration')}</div>
          </div>
        )}

        {type === 'recover' && (
          <div>
            <div className={css['LoginPage-header']}>
              <Spin spinning={isLoadingImageLogo} wrapperClassName={css['LoginPage-header__logo']}>
                {!isLoadingImageLogo && <img src={imageLogo} alt='' />}
              </Spin>
            </div>
            <div className={css['LoginPage-titleHead']}>{tRecover('forgotPassword')}</div>
            <SubTitleRender />
          </div>
        )}

        {/* Переключатель между способами входа (появляется только если есть и SAP и MOBILE provider) */}
        {sapProvider && mobileProvider && type === 'auth' && !document.location.search.includes('root') && (
          <Segmented
            className={css['LoginPage-segmented']}
            size='middle'
            block
            options={[
              {
                label: t('authMobile:loginSAP'),
                value: AUTH_TYPES.SAP,
              },
              {
                label: t('authMobile:loginPhone'),
                value: AUTH_TYPES.MOBILE,
              },
            ]}
            value={loginProvider}
            onChange={handleChangeSegment}
          />
        )}

        {/* Форма для авторизации */}
        {children}

        {/* Доп кнопки навигации (например "забыл пароль", "регистрация") */}
        {navigation()}
      </div>
    </div>
  );
};

LoginWrapper.propTypes = {
  children: PropTypes.node, // Принимаемая форма
  withoutNavigation: PropTypes.bool, // Отвечает за отображение нижних ссылок (например "забыл пароль", "регистрация")
  type: PropTypes.string, // Тип Логина, к примеру авторизации или регистрации
  isAdmin: PropTypes.bool,
};

export default LoginWrapper;
